import {
  type AllowNavigationArgs,
  FocusedFormLayout,
  FormSectionLayout,
  HasIdAndName,
  HbLoadingSpinner,
  useComputed,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { useCallback, useEffect, useRef, useState } from "react";
import { matchPath, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { createMaterialCatalogUrl, createMaterialDetailsUrl } from "src/RouteUrls";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { AiProposalStatus, MaterialType } from "src/generated/graphqlTypes";
import { NotFound } from "src/routes/NotFound";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import {
  acceptRemainingProposals,
  isListingCreatedByImport,
  listingFieldProposals,
  variantFieldProposals,
} from "src/routes/libraries/material-catalog/components/materialListingAiProposals";
import type { MaterialListing_DetailFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import type { MaterialImportAiProposal_ReviewFragment } from "src/routes/libraries/material-catalog/material-import/MaterialImportPage.generated";
import { MaterialImportReviewStep } from "src/routes/libraries/material-catalog/material-import/components/MaterialImportReviewStep";
import {
  useMaterialListingAiImportQuery,
  useMaterialListingIdForVariantQuery,
} from "src/routes/libraries/material-catalog/material-listing-page/MaterialListingPage.generated";
import { AdditionalDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/AdditionalDetailsSection";
import { MaterialDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialDetailsSection";
import {
  MaterialListingAiImportPanel,
  MaterialListingAiImportReadyModal,
  type MaterialListingAiImportState,
} from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialListingAiImportPanel";
import { useClearMaterialListingAiImportMutation } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialListingAiImportPanel.generated";
import { MaterialListingHistoryPane } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialListingHistoryPane";
import { VariantDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/VariantDetailsSection";
import {
  evictMaterialCatalogListings,
  MaterialListingByListingIdProvider,
  useMaterialListingContext,
} from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import { materialDetailsRoute } from "src/routing/domains/libraries/material-catalog/materialCatalogPaths";
import { queryResult } from "src/utils";

/**
 * The material listing detail page: one MaterialListing, with its variants selectable from a thumbnail
 * strip and the selected one addressable in the URL (`?mv=`), or create mode via `?createNew=1`.
 *
 * Old bookmarks `/libraries/material-catalog/mv:…` redirect to the listing URL with that variant selected.
 */
export function MaterialListingPage() {
  const { listingId } = useParams<{ listingId: string }>();

  if (listingId?.startsWith("mv:")) return <RedirectMaterialVariantToListing variantId={listingId} />;
  if (!listingId?.startsWith("ml:")) return <NotFound />;

  return <MaterialListingPageProviders listingId={listingId} />;
}

type RedirectMaterialVariantToListingProps = { variantId: string };

/** Compat: old `/libraries/material-catalog/mv:X` bookmarks → `/libraries/material-catalog/ml:Y?mv=mv:X`. */
function RedirectMaterialVariantToListing(props: RedirectMaterialVariantToListingProps) {
  const { variantId } = props;
  const { data, error } = useMaterialListingIdForVariantQuery({ variables: { variantId } });
  if (error) return <NotFound />;
  if (!data) return <HbLoadingSpinner />;
  return <Navigate to={createMaterialDetailsUrl(data.materialVariant.listing.id, { mv: variantId })} replace />;
}

type MaterialListingPageProvidersProps = { listingId: string };

function MaterialListingPageProviders(props: MaterialListingPageProvidersProps) {
  const { listingId } = props;
  const aiImport = useListingAiImport(listingId);
  if (!aiImport) return <HbLoadingSpinner />;
  return <MaterialListingPageForm listingId={listingId} aiImport={aiImport} />;
}

type MaterialListingPageFormProps = { listingId: string; aiImport: ListingAiImport };

function MaterialListingPageForm(props: MaterialListingPageFormProps) {
  const { listingId, aiImport } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createNew = searchParams.get("createNew") === "1";
  const mvParam = searchParams.get("mv") ?? undefined;
  // Prefer createNew when both are present; strip stray mv via replace below.
  const selectedVariantId = createNew ? undefined : mvParam?.startsWith("mv:") ? mvParam : undefined;
  // The import whose proposals the form has loaded. Loading one remounts the form, so an import that
  // lands while the user has unsaved edits waits until they've saved or discarded them.
  const [reviewingProposalId, setReviewingProposalId] = useState(() =>
    aiImport.state === "reviewing" ? aiImport.proposal?.id : undefined,
  );
  const reviewingProposal =
    aiImport.state === "reviewing" && aiImport.proposal?.id === reviewingProposalId ? aiImport.proposal : undefined;

  const onSelectVariant = useCallback(
    (id: string | undefined) => {
      if (!id) return;
      navigate(createMaterialDetailsUrl(listingId, { mv: id }), { replace: true });
    },
    [navigate, listingId],
  );

  const onCreateNew = useCallback(() => {
    navigate(createMaterialDetailsUrl(listingId, { createNew: true }), { replace: true });
  }, [navigate, listingId]);

  const onListingLoaded = useCallback(
    (listing: MaterialListing_DetailFragment) => {
      // Malformed: both createNew and mv → prefer createNew and strip mv.
      if (createNew && mvParam) {
        navigate(createMaterialDetailsUrl(listingId, { createNew: true }), { replace: true });
        return;
      }
      // Create mode: never inject a default mv.
      if (createNew) return;

      const defaultMvId = listing.defaultVariant?.id ?? listing.materialVariants.first?.id;
      const mvOnListing = selectedVariantId ? listing.materialVariants.some((v) => v.id === selectedVariantId) : false;

      // Usable mv already on the listing — nothing to canonicalize.
      if (selectedVariantId && mvOnListing) return;

      // Bare listing, unknown mv, or missing default: land on default (or stay if no variants).
      if (defaultMvId) {
        navigate(createMaterialDetailsUrl(listingId, { mv: defaultMvId }), { replace: true });
      }
    },
    [createNew, mvParam, selectedVariantId, listingId, navigate],
  );

  // Reviewing a listing the import created offers Delete Variant, and deleting the last one deletes the listing with it
  const onListingDeleted = useCallback(() => navigate(createMaterialCatalogUrl()), [navigate]);

  return (
    <MaterialListingByListingIdProvider
      // Remounting when the import is loaded re-reads the listing with the proposals mapped in.
      key={reviewingProposal?.id ?? "listing"}
      listingId={listingId}
      listingFieldProposals={reviewingProposal && listingFieldProposals(reviewingProposal)}
      variantFieldProposals={reviewingProposal && variantFieldProposals(reviewingProposal)}
      selectedVariantId={selectedVariantId}
      createNew={createNew}
      onSelectVariant={onSelectVariant}
      onCreateNew={onCreateNew}
      onListingLoaded={onListingLoaded}
      onListingDeleted={onListingDeleted}
    >
      <MaterialListingPageMetadata
        listingId={listingId}
        aiImport={aiImport}
        reviewingProposal={reviewingProposal}
        onReviewImport={setReviewingProposalId}
      />
    </MaterialListingByListingIdProvider>
  );
}

type ListingAiImport = {
  state: MaterialListingAiImportState;
  proposal: MaterialImportAiProposal_ReviewFragment | undefined;
  /** Re-reads the listing's import, after starting, resolving, or dismissing one. */
  refetch: () => Promise<unknown>;
};

/**
 * The listing's live import, polled while the agent runs, and which state the import panel is in.
 */
function useListingAiImport(listingId: string): ListingAiImport | undefined {
  const { data, error, refetch, startPolling, stopPolling } = useMaterialListingAiImportQuery({
    variables: { listingId },
  });
  const proposal = data?.materialListings.entities.first?.aiProposal ?? undefined;
  const state = error ? "idle" : data ? toAiImportState(proposal) : undefined;

  useEffect(() => {
    if (state === "processing") startPolling(10_000);
    else stopPolling();
  }, [state, startPolling, stopPolling]);

  if (!state) return undefined;
  return { state, proposal, refetch };
}

/** A fully reviewed import is soft-deleted, so the listing only needs one still worth showing. */
function toAiImportState(proposal: MaterialImportAiProposal_ReviewFragment | undefined): MaterialListingAiImportState {
  if (!proposal) return "idle";
  if (proposal.status.code === AiProposalStatus.Pending) return "processing";
  if (proposal.status.code === AiProposalStatus.Failed) return "failed";
  return "reviewing";
}

type AiImportReviewProps = {
  /** The import whose proposals the form has loaded, when there is one. */
  reviewingProposal: MaterialImportAiProposal_ReviewFragment | undefined;
  /** Loads the import's proposals into the form, which remounts it. */
  onReviewImport: (proposalId: string) => void;
};

type MaterialListingPageMetadataProps = AiImportReviewProps & { listingId: string; aiImport: ListingAiImport };

/** Items/brands/tags/markets drive the selects; loaded alongside the listing rather than per-section. */
function MaterialListingPageMetadata(props: MaterialListingPageMetadataProps) {
  const { listingId, aiImport, reviewingProposal, onReviewImport } = props;
  const query = useMaterialCatalogMetadataQuery();
  const { refetch } = query;
  // The agent can create brands and MAVs we didn't have on page load, so re-read the options once it lands.
  const isReviewingAiProposal = aiImport.state === "reviewing";
  useEffect(() => {
    if (isReviewingAiProposal) refetch().catch(() => undefined);
  }, [isReviewingAiProposal, refetch]);
  return queryResult(query, ({ items, materialBrands, materialVariantTags, markets }) => (
    <MaterialListingPageView
      listingId={listingId}
      aiImport={aiImport}
      reviewingProposal={reviewingProposal}
      onReviewImport={onReviewImport}
      items={items}
      brands={materialBrands}
      tags={materialVariantTags}
      markets={markets}
    />
  ));
}

type MaterialListingPageViewProps = AiImportReviewProps & {
  listingId: string;
  aiImport: ListingAiImport;
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
};

function MaterialListingPageView(props: MaterialListingPageViewProps) {
  const { listingId, aiImport, reviewingProposal, onReviewImport, items, brands, tags, markets } = props;
  const { listing, formState, loading, save, addVariant } = useMaterialListingContext();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [clearImport] = useClearMaterialListingAiImportMutation();
  const { state: importState, proposal, refetch: refetchImport } = aiImport;
  const isReviewingAiProposal = !!reviewingProposal;
  // An import that has landed, but whose proposals the form hasn't loaded yet
  const landedProposalId = importState === "reviewing" && !reviewingProposal ? proposal?.id : undefined;
  // A listing the import created is reviewed on an AI page of its own, rather than under the import panel
  const importedListingProposal =
    reviewingProposal && isListingCreatedByImport(reviewingProposal) ? reviewingProposal : undefined;
  const isReviewingImportedListing = !!importedListingProposal;

  const canSubmit = useComputed(
    () => (formState.dirty || isReviewingAiProposal) && formState.valid,
    [formState, isReviewingAiProposal],
  );

  // Set once an imported listing's review is saved, so the hand-off to the catalog isn't read as leaving edits behind
  const reviewSaved = useRef(false);

  const onSave = useCallback(async () => {
    // Touch the fields so any validation error is visible, the way `SubmitButton` did.
    if (!formState.canSave()) return;
    // Applies each remaining proposal's value and resolves its status
    if (isReviewingAiProposal) acceptRemainingProposals(formState, { items, brands });
    if (isReviewingImportedListing) {
      // Accepting a listing the import created finishes it, so there is nothing left to review here
      if (!(await save({ successMessage: "1 AI-generated material has been accepted." }))) return;
      reviewSaved.current = true;
      navigate(createMaterialCatalogUrl());
      return;
    }
    const saved = await save();
    // Resolving every proposal soft deletes the import, so re-read it to put the panel back to the url form
    if (saved && isReviewingAiProposal) await refetchImport();
    // With the edits saved, a landed import can be loaded without dropping anything
    if (saved && landedProposalId) onReviewImport(landedProposalId);
  }, [
    formState,
    save,
    isReviewingAiProposal,
    isReviewingImportedListing,
    items,
    brands,
    refetchImport,
    landedProposalId,
    onReviewImport,
    navigate,
  ]);

  // Loads a landed import into the form -- asking first when there are unsaved edits the remount would drop.
  // Asked once per import: declining Save leaves the import for the page's own Save to load.
  const askedForProposalId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!landedProposalId || askedForProposalId.current === landedProposalId) return;
    askedForProposalId.current = landedProposalId;
    if (!formState.dirty) return onReviewImport(landedProposalId);
    openModal({
      allowClosing: false,
      aiMode: true,
      content: (
        <MaterialListingAiImportReadyModal
          onDiscard={() => onReviewImport(landedProposalId)}
          onSave={async () => {
            if (formState.canSave() && (await save())) onReviewImport(landedProposalId);
            closeModal();
          }}
        />
      ),
    });
  }, [landedProposalId, formState, save, openModal, closeModal, onReviewImport]);

  const onCancel = useCallback(() => {
    navigate(createMaterialCatalogUrl());
  }, [navigate]);

  // Clearing an import that created the listing deletes the listing, so we must navigate away
  const onRemove = useCallback(async () => {
    if (!importedListingProposal) return;
    await clearImport({ variables: { id: importedListingProposal.id }, update: evictMaterialCatalogListings });
    triggerNotice({ message: "1 AI-generated material has been removed.", icon: "success" });
    navigate(createMaterialCatalogUrl());
  }, [clearImport, importedListingProposal, triggerNotice, navigate]);

  // Selecting a thumbnail or entering create mode only changes the search params, which is still this
  // same form — only leaving the listing page should trigger a confirmation.
  const allowNavigation = useCallback(
    ({ nextLocation }: AllowNavigationArgs) =>
      matchPath(materialDetailsRoute.path, nextLocation.pathname)?.params.listingId === listingId,
    [listingId],
  );

  // Only products can be imported via AI
  const canAiImport = listing?.type.code === MaterialType.Product;

  const isFormReady = useComputed(() => formState.id.value === listing?.id, [formState, listing]);

  if (!listing) return loading ? <HbLoadingSpinner /> : <NotFound />;
  if (!isFormReady) return <HbLoadingSpinner />;

  return (
    <FocusedFormLayout
      aiMode={isReviewingImportedListing}
      title={listing.name ?? ""}
      breadcrumbs={{ breadcrumbs: { label: "Materials Catalog", href: createMaterialCatalogUrl() } }}
      onCancel={onCancel}
      completeLabel="Save"
      onComplete={onSave}
      primaryDisabled={!canSubmit}
      isDirty={() => !reviewSaved.current && formState.dirty}
      allowNavigation={allowNavigation}
      destructiveAction={
        importedListingProposal
          ? {
              label: "Remove",
              confirmTitle: "Warning: This Action Cannot be Undone",
              confirmMessage: "Are you sure you want to remove this material from the material catalog?",
              onConfirm: onRemove,
            }
          : undefined
      }
      rightPaneTriggers={[
        { icon: "history", label: "History", content: <MaterialListingHistoryPane materialListingId={listingId} /> },
      ]}
    >
      {importedListingProposal ? (
        <MaterialImportReviewStep
          items={items}
          brands={brands}
          tags={tags}
          markets={markets}
          title="Review AI-Generated Material"
          description="Please review the AI-generated data highlighted below. You may edit or add to the form before saving."
          withJumpLinks
        />
      ) : (
        <FormSectionLayout
          title="Material Details"
          initialFields={
            <MaterialDetailsSection
              items={items}
              brands={brands}
              aiImportPanel={
                canAiImport && (
                  <MaterialListingAiImportPanel
                    listing={listing}
                    state={landedProposalId ? "processing" : importState}
                    proposal={proposal}
                  />
                )
              }
            />
          }
          withJumpLinks
          includeTitleJumpLink
          sections={[
            {
              title: "Variant Details",
              actions: [{ label: "Add Variant", variant: "secondary", icon: "plus", onClick: addVariant }],
              fields: <VariantDetailsSection items={items} tags={tags} />,
            },
            { title: "Additional Details", fields: <AdditionalDetailsSection markets={markets} /> },
          ]}
        />
      )}
    </FocusedFormLayout>
  );
}
